import React from 'react';
import { motion } from 'framer-motion';
import { WHEEL_ORDER, getNumberColor } from '../utils/roulette';

export const RouletteWheel = ({ isSpinning, lastWinningNumber, isAmbient = false }) => {
  const TOTAL_NUMBERS = 37;
  const ANGLE_PER_NUMBER = 360 / TOTAL_NUMBERS;

  const getRotationAngle = () => {
    if (lastWinningNumber === null || lastWinningNumber === undefined) return 0;
    const numberIndex = WHEEL_ORDER.indexOf(Number(lastWinningNumber));
    if (numberIndex === -1) return 0;

    // Angle of target segment
    const targetAngle = numberIndex * ANGLE_PER_NUMBER;
    const extraSpins = 360 * 6;
    return -(extraSpins + targetAngle);
  };

  return (
    <div className="relative w-full h-full mx-auto p-2 select-none">
      {/* Top Gold Pointer / Marker */}
      <div className="absolute top-[-12px] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[24px] border-l-transparent border-r-transparent border-t-[#e5b849] z-40 drop-shadow-[0_6px_8px_rgba(0,0,0,0.9)]" />

      {/* Main Wheel Wrapper with Glow */}
      <div className="relative w-full h-full rounded-full shadow-[0_20px_50px_rgba(0,0,0,0.95)] border-4 border-[#b8860b]">
        {/* Rotating Wheel Container */}
        <motion.div
          className="w-full h-full rounded-full overflow-hidden relative"
          initial={{ rotate: 0 }}
          animate={{
            rotate: isSpinning
              ? [0, -2160]
              : isAmbient
                ? [0, 360]
                : getRotationAngle()
          }}
          transition={
            isSpinning
              ? { duration: 4, ease: "linear", repeat: Infinity }
              : isAmbient
                ? { duration: 60, ease: "linear", repeat: Infinity }
                : { duration: 4, ease: [0.15, 0.85, 0.25, 1] }
          }
        >
          {/* Vector SVG Roulette Wheel */}
          <svg viewBox="0 0 500 500" className="w-full h-full">
            <defs>
              {/* Gold Gradients */}
              <radialGradient id="goldOuter" cx="50%" cy="50%" r="50%">
                <stop offset="70%" stopColor="#2a1f0a" />
                <stop offset="90%" stopColor="#8c6d1f" />
                <stop offset="100%" stopColor="#e5b849" />
              </radialGradient>
              <linearGradient id="goldRim" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#fbe6a2" />
                <stop offset="50%" stopColor="#b8860b" />
                <stop offset="100%" stopColor="#634503" />
              </linearGradient>
              <radialGradient id="centerCone" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#fff2c4" />
                <stop offset="40%" stopColor="#d4af37" />
                <stop offset="85%" stopColor="#59430c" />
                <stop offset="100%" stopColor="#1a1302" />
              </radialGradient>
            </defs>

            {/* Outer Rim */}
            <circle cx="250" cy="250" r="246" fill="url(#goldOuter)" stroke="url(#goldRim)" strokeWidth="8" />
            <circle cx="250" cy="250" r="232" fill="#140e05" stroke="#42310b" strokeWidth="3" />

            {/* Pockets */}
            <g transform="translate(250,250)">
              {WHEEL_ORDER.map((num, idx) => {
                const angle = idx * ANGLE_PER_NUMBER;
                const color = getNumberColor(num);
                const fillColor = color === 'red' ? '#b91c1c' : color === 'black' ? '#111827' : '#047857';

                const startRad = ((angle - ANGLE_PER_NUMBER / 2) * Math.PI) / 180;
                const endRad = ((angle + ANGLE_PER_NUMBER / 2) * Math.PI) / 180;

                const rInner = 140;
                const rOuter = 228;

                const x1 = rOuter * Math.sin(startRad);
                const y1 = -rOuter * Math.cos(startRad);
                const x2 = rOuter * Math.sin(endRad);
                const y2 = -rOuter * Math.cos(endRad);

                const x3 = rInner * Math.sin(endRad);
                const y3 = -rInner * Math.cos(endRad);
                const x4 = rInner * Math.sin(startRad);
                const y4 = -rInner * Math.cos(startRad);

                const pathData = `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 0 0 ${x4} ${y4} Z`;

                // Number text position
                const textR = 192;
                const textX = textR * Math.sin((angle * Math.PI) / 180);
                const textY = -textR * Math.cos((angle * Math.PI) / 180);

                return (
                  <g key={num}>
                    <path d={pathData} fill={fillColor} stroke="#d4af37" strokeWidth="1" />
                    <text
                      x={textX}
                      y={textY}
                      fill="#ffffff"
                      fontSize="14"
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="central"
                      transform={`rotate(${angle}, ${textX}, ${textY})`}
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {num}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Inner Ring Divider */}
            <circle cx="250" cy="250" r="140" fill="none" stroke="url(#goldRim)" strokeWidth="3" />
            <circle cx="250" cy="250" r="100" fill="#0d0e12" stroke="#d4af37" strokeWidth="2" />

            {/* Brass Center Turret */}
            <circle cx="250" cy="250" r="70" fill="url(#centerCone)" stroke="#fbe6a2" strokeWidth="2" />
            <circle cx="250" cy="250" r="30" fill="url(#goldRim)" />
            <circle cx="250" cy="250" r="12" fill="#fff" opacity="0.8" />
          </svg>

          {/* Glossy Overlay */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/10 via-transparent to-black/50 pointer-events-none" />
        </motion.div>
      </div>

      {/* Marble/Ball */}
      <div className="absolute inset-0 pointer-events-none z-50 flex items-center justify-center">
        <motion.div
          className="w-5 h-5 rounded-full"
          initial={{ opacity: 0 }}
          animate={{
            opacity: isSpinning ? 1 : lastWinningNumber !== null ? 1 : 0,
            scale: isSpinning ? [1, 1.25, 1] : 1
          }}
          transition={{ duration: 0.3, repeat: isSpinning ? Infinity : 0 }}
          style={{
            transform: 'translateY(-175px)'
          }}
        >
          {/* Marble shadow */}
          <div className="absolute top-1 left-1 w-full h-full rounded-full bg-black/70 blur-[2px]" />
          {/* Silver ball */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white via-[#e2e8f0] to-[#64748b] shadow-[0_0_10px_rgba(255,255,255,0.9)]" />
        </motion.div>
      </div>
    </div>
  );
};
